// @ts-nocheck
import {useState, useEffect, useRef, DragEvent} from 'react'
import {createPortal} from 'react-dom'
import {Link} from 'react-router-dom'
import {getUnreadCount, createTicket} from '../../pages/support/core/_requests'
import './SupportWidget.scss'

const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'application/pdf'
]

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25MB

// Customer-service WhatsApp, the same number the onboarding wizard, the tutorial and
// the sidebar use. 0722210190 → international (drop the leading 0, prepend 972).
const SUPPORT_WA_LINK = `https://wa.me/972722210190?text=${encodeURIComponent('שלום, אני צריך/ה עזרה 🙏')}`

function SupportWidgetContent() {
  const [isOpen, setIsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadUnreadCount()
    const interval = setInterval(loadUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [])

  const loadUnreadCount = async () => {
    try {
      const response = await getUnreadCount()
      setUnreadCount(response.data.count)
    } catch (err) {
      console.error('Failed to load unread count:', err)
    }
  }

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      return `סוג קובץ לא נתמך: ${file.name}`
    }
    if (file.size > MAX_FILE_SIZE) {
      return `הקובץ גדול מדי (מקסימום 25MB): ${file.name}`
    }
    return null
  }

  const handleFiles = (newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles)
    const errors: string[] = []
    const validFiles: File[] = []

    fileArray.forEach(file => {
      const error = validateFile(file)
      if (error) {
        errors.push(error)
      } else {
        validFiles.push(file)
      }
    })

    if (errors.length > 0) {
      setError(errors.join('\n'))
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles])
    }
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return 'bi-image'
    if (file.type.startsWith('video/')) return 'bi-camera-video'
    if (file.type === 'application/pdf') return 'bi-file-pdf'
    return 'bi-file-earmark'
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!subject.trim() || !message.trim()) {
      setError('נא למלא את כל השדות')
      return
    }

    setSending(true)
    setError('')

    try {
      await createTicket({subject, message, attachments: files})
      setSuccess(true)
      setSubject('')
      setMessage('')
      setFiles([])
      loadUnreadCount()
      setTimeout(() => {
        setSuccess(false)
        setShowForm(false)
        setIsOpen(false)
      }, 2500)
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'שגיאה בשליחת הפנייה')
    } finally {
      setSending(false)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setShowForm(false)
    setSuccess(false)
    setError('')
    setFiles([])
  }

  return (
    <div className="support-widget">
      {/* WhatsApp, alongside the ticket button. A ticket is the right channel for
          something that needs a trail; plenty of questions just want a quick answer,
          and people will look for WhatsApp before they'll open a form. Hidden while the
          panel is open so it doesn't sit on top of it. */}
      {!isOpen && (
        <a
          className="support-widget-wa"
          href={SUPPORT_WA_LINK}
          target="_blank"
          rel="noreferrer"
          title="תמיכה בוואטסאפ"
          aria-label="תמיכה בוואטסאפ"
        >
          <i className="bi bi-whatsapp"></i>
        </a>
      )}

      {/* Floating Button */}
      <button
        className={`support-widget-button ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="תמיכה"
      >
        {isOpen ? (
          <i className="bi bi-x-lg"></i>
        ) : (
          <>
            <i className="bi bi-headset"></i>
            {unreadCount > 0 && (
              <span className="support-widget-badge">{unreadCount}</span>
            )}
          </>
        )}
      </button>

      {/* Popup Panel */}
      {isOpen && (
        <div className="support-widget-panel">
          <div className="support-widget-header">
            <div>
              <h5>מרכז התמיכה</h5>
              <span className="support-widget-subtitle">נשמח לעזור!</span>
            </div>
            <button className="support-widget-close" onClick={handleClose}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {success ? (
            <div className="support-widget-body support-widget-success">
              <div className="success-icon">
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <h6>הפנייה נשלחה בהצלחה!</h6>
              <p>נחזור אליך בהקדם האפשרי</p>
            </div>
          ) : showForm ? (
            <form onSubmit={handleSubmit} className="support-widget-body support-widget-form">
              {error && (
                <div className="support-widget-error">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <span>{error}</span>
                </div>
              )}

              <div className="form-group">
                <label>נושא הפנייה</label>
                <input
                  type="text"
                  placeholder="למשל: שאלה לגבי התשלום"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>תוכן ההודעה</label>
                <textarea
                  rows={4}
                  placeholder="ספר לנו איך נוכל לעזור..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              {/* Drag and Drop Area */}
              <div
                className={`drop-zone ${isDragging ? 'dragging' : ''} ${files.length > 0 ? 'has-files' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf"
                  onChange={(e) => e.target.files && handleFiles(e.target.files)}
                  style={{display: 'none'}}
                />
                {files.length === 0 ? (
                  <>
                    <i className="bi bi-cloud-arrow-up"></i>
                    <span>גרור קבצים לכאן</span>
                    <small>תמונות, סרטונים או PDF (עד 25MB)</small>
                  </>
                ) : (
                  <div className="files-list">
                    {files.map((file, index) => (
                      <div key={index} className="file-item">
                        <i className={getFileIcon(file)}></i>
                        <span className="file-name">{file.name}</span>
                        <button
                          type="button"
                          className="file-remove"
                          onClick={(e) => {
                            e.stopPropagation()
                            removeFile(index)
                          }}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    ))}
                    <div className="add-more">
                      <i className="bi bi-plus"></i>
                      <span>הוסף עוד</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>
                  ביטול
                </button>
                <button type="submit" className="btn-submit" disabled={sending}>
                  {sending ? (
                    <span className="spinner"></span>
                  ) : (
                    <>
                      <i className="bi bi-send"></i>
                      שלח
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="support-widget-body support-widget-menu">
              <button className="menu-item primary" onClick={() => setShowForm(true)}>
                <div className="menu-icon">
                  <i className="bi bi-pencil-square"></i>
                </div>
                <div className="menu-text">
                  <span className="menu-title">פנייה חדשה</span>
                  <span className="menu-subtitle">שלח לנו הודעה</span>
                </div>
                <i className="bi bi-chevron-left"></i>
              </button>

              <Link to="/support" className="menu-item" onClick={handleClose}>
                <div className="menu-icon">
                  <i className="bi bi-inbox"></i>
                </div>
                <div className="menu-text">
                  <span className="menu-title">הפניות שלי</span>
                  <span className="menu-subtitle">צפה בהיסטוריית הפניות</span>
                </div>
                {unreadCount > 0 && (
                  <span className="menu-badge">{unreadCount}</span>
                )}
                <i className="bi bi-chevron-left"></i>
              </Link>

              <div className="support-info">
                <i className="bi bi-clock"></i>
                <span>זמני מענה: א'-ה' 9:00-18:00</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function SupportWidget() {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null

  // Hide support widget when admin is logged in
  const adminToken = localStorage.getItem('admin_auth_token')
  if (adminToken) return null

  return createPortal(<SupportWidgetContent />, document.body)
}
